/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    IContentBlockAdditionalContentItemViewProps,
    IContentBlockAdditionalContentViewProps,
    IContentBlockViewProps,
    IContentBlockFullConfig
} from '@msdyn365-commerce-modules/content-block';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { useIsMobile } from '../../../shared/hooks/useIsMobile';
import { IContentBlockProps } from '../definition-extensions/content-block.ext.props.autogenerated';

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (additionalContent: IContentBlockAdditionalContentViewProps) => {
    return (
        <Node {...additionalContent.additionalContentNode}>
            {additionalContent.additionalContentItems?.map((item: IContentBlockAdditionalContentItemViewProps) => {
                return (
                    <>
                        {item.heading}
                        <Node {...item.additionalContentItemContainer}>
                            {item.text}
                            <Node {...item.additionalContentItemLinks}>{item.links}</Node>
                        </Node>
                    </>
                );
            })}
        </Node>
    );
};

/**
 * Render View.
 * @param props - The view props.
 * @returns -The JSX Element.
 */
const contentBlockView: React.FC<IContentBlockViewProps & IContentBlockProps<{}>> = props => {
    const {
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        title,
        text,
        links,
        image,
        contentBlockAnchorTag,
        imageLink,
        imageAriaLabel,
        additionalContent,
        config,
        context
    } = props;


    // @ts-expect-error HTML element need to be clear on run time.
    const hasImage: boolean = image.props.src;
    const imageClass: string = hasImage ? `${detailsContainer.className} withImage` : `${detailsContainer.className} withoutImage`;

    const mobileImageProps = {
        gridSettings: context.request.gridSettings ?? {},
        imageSettings: config.mobileImage?.imageSettings
    };

    const mobileImage = (
        <Msdyn365.Image
            {...config.mobileImage}
            {...mobileImageProps}
            requestContext={context.request}
            preserveImageSpace={true}
            className='ms-content-block__mobile-image'
            editProps={{
                key: config.mobileImage ?? {},
                requestContext: context.request,
                moduleType: props.typeName,
                imagePropertyName: 'mobileImage',
                moduleId: props.id,
                layout: (config as IContentBlockFullConfig).msdyn365__moduleLayout
            }}
            shouldSkipToMainImage
        />
    );

    const isMobile = useIsMobile(context);

    if (imageLink) {
        return (
            <Module {...contentBlockContainer}>
                <Node
                    {...contentBlockAnchorTag}
                    href={imageLink}
                    className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                    aria-label={imageAriaLabel}
                >
                    {isMobile && config.mobileImage ? (
                        <Node {...imageContainer}>{mobileImage}</Node>
                    ) : (
                        <Node {...imageContainer}>{image}</Node>
                    )}
                </Node>
                <Node {...detailsContainer}>
                    {title}
                    {text}
                    {links}
                    {additionalContent && renderAdditionalContent(additionalContent)}
                </Node>
            </Module>
        );
    }
    return (
        <Module {...contentBlockContainer}>
            {isMobile && config.mobileImage ? <Node {...imageContainer}>{mobileImage}</Node> : <Node {...imageContainer}>{image}</Node>}
            <Node className={imageClass}>
                {title}
                {text}
                {links}
                {additionalContent && renderAdditionalContent(additionalContent)}
            </Node>
        </Module>
    );
};

export default contentBlockView;
